/**
 * The blackjack driver — the poker Director's shape, small.
 *
 * ============================== WHAT THIS IS ===================================
 * `routes/Blackjack.tsx` ran the table with `runBots`: a `while` loop that played every
 * seat to the hero's turn in one tick, and a felt that re-rendered from the mutated
 * table. Four seats' decisions, the dealer's whole hand and the settlement all landed in
 * the same frame. This turns that into time, the way `director.ts` does for poker, and
 * with the same three rules:
 *
 *   1. THE ENGINE IS UNTOUCHED, AND IS CALLED IN THE SAME ORDER. `startHand`, the bots'
 *      `takeInsurance`, the hero's `takeInsurance` + `closeInsurance`, then `act` for
 *      each decision — exactly the sequence `runBots` made, one call at a time. The
 *      bots' own generator (`CryptoRng(seed, "bots")`, sitting-level, as before) is
 *      drawn from in the same order; the pacing has a generator of its own
 *      (`driverRng`, seeded from the sitting's seed) and never touches the engine's.
 *      `blackjack-director.test.ts` holds a driven sitting to an undriven one, seat by
 *      seat, hand by hand.
 *   2. NOTHING HERE TOUCHES THE DOM. Timers go through `Clock` so a test can play fifty
 *      hands in no time; what the scene needs comes out through `onChange` as a plain
 *      snapshot, and every timed thing is announced through `onStep` for the flight
 *      layer before its state lands.
 *   3. THE SCENE RENDERS FROM THE PRESENTED VIEW. The engine deals a whole round in
 *      `startHand` and plays the dealer out inside the last `act`; the view the scene
 *      draws (`blackjack-view.ts`) catches up a card at a time.
 *
 * ## Where the time goes
 *
 * The deal: one card at a time, seat by seat then the dealer, twice round — poker's
 * 120 ms flight and 80 ms stagger. A seat's decision: `bjThinkTime` (1–2 s, +0.5 on a
 * split or double, scaled by pace) with its clock running, then the card it drew. The
 * hero: ten quiet seconds and a five-second hurry-up, and when it runs out, Stand
 * (Decline, if it is the insurance question) — through the same path as a click. The
 * dealer: a 600 ms beat, the hole card turns, and each card they draw comes on its own.
 * Then each hand settles in turn and the chips move.
 * ===============================================================================
 */
import type { BlackjackTable, Action } from "@engine/blackjack/table";
import { botAction, botTakesInsurance, type Fidelity } from "@engine/blackjack/bots";
import { snapshot as tableSnapshot, type TableSnapshot } from "@engine/blackjack/snapshot";
import type { CryptoRng } from "@engine/rng";
import { realClock, driverRng, type Clock, type SeatClock } from "./director";
import { HUMAN_CLOCK, bjThinkTime, type BjDurations } from "./pace";
import { viewOf, cloneView, emptyView, type BjView, type HeroMove, type Outcome } from "./blackjack-view";

export type { Clock };

/* ------------------------------------------------------------------ steps --- */

/**
 * One timed thing. `dur` is how long before the next step starts; `land` how long
 * before this one's state is applied (a dealt card is in the air for `land`, and the
 * next leaves after `dur`, so two are travelling at once — poker's deal, exactly).
 */
export type BjStep =
  /** The last hand's cards go to the discard tray and the circles empty. */
  | { kind: "collect"; dur: number; land: number }
  /** The tray goes back into the shoe: two fresh decks, every hand. */
  | { kind: "shuffle"; dur: number; land: number }
  /** A seat's opening bet slides into its circle. `amount` in bet units. */
  | { kind: "bet"; dur: number; land: number; seat: number; amount: number }
  /** A card leaves the shoe. `seat` -1 is the dealer. */
  | { kind: "deal"; dur: number; land: number; seat: number; hand: number; index: number; faceDown: boolean }
  | { kind: "insure"; dur: number; land: number; seat: number; amount: number }
  /** A seat's clock. The decision follows as its own step. */
  | { kind: "think"; dur: number; land: number; seat: number; hand: number; ordinal: number }
  | { kind: "act"; dur: number; land: number; seat: number; hand: number; action: Action; ordinal: number }
  /** A double's or a split's second bet. */
  | { kind: "chips"; dur: number; land: number; seat: number; hand: number; amount: number }
  /** A pair comes apart into two hands. */
  | { kind: "split"; dur: number; land: number; seat: number; hand: number }
  /** A held breath — the dealer about to turn the hole card. */
  | { kind: "beat"; dur: number; land: number }
  | { kind: "flip"; dur: number; land: number }
  /** A hand settles. `returned` is what comes back to the seat in bet units: stake plus result. */
  | { kind: "settle"; dur: number; land: number; seat: number; hand: number; outcome: Outcome; returned: number; net: number }
  | { kind: "insurance"; dur: number; land: number; seat: number; returned: number; won: boolean }
  /** The hero is to act. The timeline stops here until `act()`. */
  | { kind: "await"; dur: number; land: number; seat: number; what: "insurance" | "act" }
  | { kind: "hand_over"; dur: number; land: number };

export type BjStepKind = BjStep["kind"];

interface Queued { step: BjStep; after: BjView }

/* --------------------------------------------------------------- snapshot --- */

export type BjPhase = "idle" | "playing" | "awaiting" | "between";

export interface BjSnapshot {
  view: BjView | null;
  phase: BjPhase;
  /** What the hero is being asked, while `phase` is `awaiting`. */
  awaiting: "insurance" | "act" | null;
  clocks: Record<number, SeatClock>;
  waiting: { seat: number; secondsLeft: number } | null;
  heroClock: { hurry: boolean; secondsLeft: number } | null;
  lastAction: { seat: number; hand: number; action: HeroMove } | null;
}

export interface BjDirectorOptions {
  table: BlackjackTable;
  fidelity: Fidelity;
  /** The bots' own generator — `CryptoRng(seed, "bots")`, one per sitting, as `runBots` had it. */
  botRng: CryptoRng;
  /** Seeds the pacing generator. Never the engine's. */
  seed: number;
  durations: () => BjDurations;
  onChange: (snap: BjSnapshot) => void;
  onStep?: (step: BjStep, before: BjView) => void;
  onTick?: (secondsLeft: number) => void;
  /** A seat has decided, before the engine is told: the room's lesson hook. */
  onBotDecision?: (d: { seat: number; hand: number; action: Action; ordinal: number; before: TableSnapshot; handNumber: number }) => void;
  /** The hand has been PRESENTED to its end — the chips have landed. */
  onHandOver?: (t: BlackjackTable) => void;
  /** The hero's clock ran out and this is the move it is about to make for them — so the
      room can grade it and account for it exactly as it does a click. */
  onExpire?: (move: HeroMove) => void;
  /** Tests and drills override the clock; null is no clock at all. */
  humanClock?: { quiet: number; hurry: number } | null;
  clock?: Clock;
}

/* --------------------------------------------------------------- director --- */

export class BlackjackDirector {
  private readonly t: BlackjackTable;
  private readonly fidelity: Fidelity;
  private readonly botRng: CryptoRng;
  private readonly rng: () => number;
  private readonly durations: () => BjDurations;
  private readonly clock: Clock;
  private readonly opts: BjDirectorOptions;
  private readonly humanClock: { quiet: number; hurry: number } | null;

  private presented: BjView;
  /** The view at the END of everything queued. */
  private shadow: BjView;
  private queue: Queued[] = [];
  private running = false;
  private destroyed = false;
  private awaitingWhat: "insurance" | "act" | null = null;
  private phase: BjPhase = "idle";
  private clocks: Record<number, SeatClock> = {};
  private waiting: BjSnapshot["waiting"] = null;
  private heroClock: BjSnapshot["heroClock"] = null;
  private lastAction: BjSnapshot["lastAction"] = null;
  private readonly timers = new Set<unknown>();
  private readonly clockTimers = new Set<unknown>();
  private ordinal = 0;
  private stepsSeen = 0;
  /** This hand's dealer and settlement are already on the timeline. */
  private endQueued = false;

  constructor(opts: BjDirectorOptions) {
    this.opts = opts;
    this.t = opts.table;
    this.fidelity = opts.fidelity;
    this.botRng = opts.botRng;
    this.rng = driverRng(opts.seed);
    this.durations = opts.durations;
    this.clock = opts.clock ?? realClock;
    this.humanClock = opts.humanClock === undefined
      ? { quiet: HUMAN_CLOCK.quiet, hurry: HUMAN_CLOCK.hurry } : opts.humanClock;
    this.presented = emptyView(this.t.seats.length);
    this.shadow = cloneView(this.presented);
  }

  /* ------------------------------------------------------------ public --- */

  snapshot(): BjSnapshot {
    return {
      view: this.presented, phase: this.phase, awaiting: this.awaitingWhat,
      clocks: { ...this.clocks }, waiting: this.waiting, heroClock: this.heroClock,
      lastAction: this.lastAction,
    };
  }

  get stepCount(): number { return this.stepsSeen; }
  get isAwaiting(): boolean { return this.awaitingWhat !== null; }
  get isIdle(): boolean { return !this.running && this.awaitingWhat === null && this.queue.length === 0; }
  /** The engine's table. The room reads it for the analysis and the settlement, never for the felt. */
  get table(): BlackjackTable { return this.t; }

  /**
   * Deal the next hand at `bet` units a seat.
   *
   * Refuses while a hand is still being presented or is waiting on the hero — the
   * "Next hand" button and a double-tap can both fire, and a deal that starts while the
   * last payout is in the air is the reflow this layer exists to prevent.
   */
  deal(bet: number): boolean {
    if (this.destroyed || !this.isIdle) return false;
    if (this.t.handNumber > 0 && this.t.phase !== "settled") return false;
    const d = this.durations();
    /* The old hand leaves the felt, and the shoe is refilled. */
    if (this.shadow.seats.some((s) => s.hands.length) || this.shadow.dealer.length) {
      this.push({ kind: "collect", dur: d.potCollect, land: 0 },
        { ...emptyView(this.t.seats.length, this.shadow.handNumber) });
    }
    /* === the engine, in runBots's order: deal, then the bots answer insurance === */
    this.endQueued = false;
    this.t.startHand(bet);
    if (this.t.phase === "insurance") {
      for (let i = 0; i < this.t.seats.length; i++) {
        if (this.t.seats[i]!.isHero) continue;
        this.t.takeInsurance(i, botTakesInsurance(this.fidelity, this.botRng));
      }
    }
    const full = viewOf(this.t);
    const n = this.t.seats.length;
    let v = emptyView(n, full.handNumber);
    this.push({ kind: "shuffle", dur: d.shuffle, land: 0 }, v);

    /* Every seat's chip goes out — a beat apart, landing together. */
    this.t.seats.forEach((s, seat) => {
      v = cloneView(v);
      v.seats[seat]!.hands = [{ cards: [], bet, doubled: false, fromSplit: false, outcome: null, settled: null }];
      this.push({ kind: "bet", dur: d.collectStagger, land: d.betSlide, seat, amount: bet }, v);
    });
    this.settle(d.betSlide);

    /* One card to each seat, one to the dealer, then round again; the second is the hole. */
    for (let round = 0; round < 2; round++) {
      for (let seat = 0; seat < n; seat++) {
        v = cloneView(v);
        v.seats[seat]!.hands[0]!.cards.push(full.seats[seat]!.hands[0]!.cards[round]!);
        this.push({ kind: "deal", dur: d.dealStagger, land: d.deal, seat, hand: 0, index: round, faceDown: false }, v);
      }
      v = cloneView(v);
      v.dealer.push(round === 0 ? full.dealer[0]! : null);
      this.push({ kind: "deal", dur: d.dealStagger, land: d.deal, seat: -1, hand: 0, index: round, faceDown: round === 1 }, v);
    }
    this.settle(d.deal);

    if (this.t.phase === "insurance") {
      /* The seats that took it put their half-chip on the line, then the hero is asked. */
      for (const s of full.seats) {
        if (s.isHero || s.insurance <= 0) continue;
        v = cloneView(v);
        v.seats[s.index]!.insurance = s.insurance;
        this.push({ kind: "insure", dur: d.betSlide, land: d.betSlide, seat: s.index, amount: s.insurance }, v);
      }
      v = cloneView(v);
      v.phase = "insurance";
      this.push({ kind: "await", dur: 0, land: 0, seat: 0, what: "insurance" }, v);
      this.startPlayback();
      return true;
    }
    this.afterPeek();
    this.pump();
    return true;
  }

  /**
   * The hero's move, from the bar or from the clock running out. Applied to the engine
   * at once, then presented at pace like everything else.
   */
  act(move: HeroMove): boolean {
    if (this.destroyed || this.awaitingWhat === null) return false;
    const what = this.awaitingWhat;
    if (what === "insurance" && move !== "insurance" && move !== "decline") return false;
    if (what === "act" && (move === "insurance" || move === "decline")) return false;
    this.stopHumanClock();
    const d = this.durations();
    try {
      if (what === "insurance") {
        this.t.takeInsurance(0, move === "insurance");
        this.t.closeInsurance();
      } else {
        const turn = this.t.turn!;
        const before = viewOf(this.t);
        this.t.act(move as Action);
        this.awaitingWhat = null;
        this.heroClock = null;
        this.clocks = {};
        this.phase = "playing";
        this.lastAction = { seat: turn.seat, hand: turn.hand, action: move };
        this.ingestAction(turn.seat, turn.hand, move as Action, before, 0);
        this.pump();
        return true;
      }
    } catch (err) {
      /* Refused by the engine (the bar only offers legal moves, so this is a bug): the
         decision stays open and the clock restarts rather than stranding the hand. */
      this.startHumanClock();
      throw err;
    }
    /* Insurance answered. */
    this.awaitingWhat = null;
    this.heroClock = null;
    this.clocks = {};
    this.phase = "playing";
    this.lastAction = { seat: 0, hand: 0, action: move };
    const full = viewOf(this.t);
    let v = cloneView(this.shadow);
    if (full.seats[0]!.insurance > 0) {
      v.seats[0]!.insurance = full.seats[0]!.insurance;
      this.push({ kind: "insure", dur: d.betSlide, land: d.betSlide, seat: 0, amount: full.seats[0]!.insurance }, v);
    }
    /*
     * The dealer peeks. A natural gets its own beat before the hole card turns, in
     * `afterPeek`; a peek that finds nothing is a beat of its own, and then the
     * insurance chips go to the house.
     */
    if (this.t.dealerNatural) {
      this.shadow = cloneView(v);
    } else {
      v = cloneView(v);
      v.phase = "acting";
      this.push({ kind: "beat", dur: d.holeBeat, land: 0 }, v);
    }
    this.afterPeek();
    this.pump();
    return true;
  }

  /** Leaving the table. Every timer dies here; nothing fires after this returns. */
  destroy(): void {
    this.destroyed = true;
    this.stopHumanClock();
    for (const h of this.timers) this.clock.clearTimeout(h);
    this.timers.clear();
    this.queue = [];
    this.running = false;
  }

  /* ----------------------------------------------------------------- loop --- */

  /**
   * THE STEPPED `runBots`.
   *
   * The same loop — while a seat that is not the hero has the turn, ask `botAction` and
   * `act` on it — with the time a seat takes put in front of each decision: a `think`
   * step with that seat's clock, then the move and the card it drew. When the hero has
   * the turn the timeline stops at `await`; when the engine has played the dealer out,
   * the dealer's hand and the settlement are queued and the hand ends.
   */
  private pump(): void {
    const t = this.t;
    let guard = 0;
    for (;;) {
      if (++guard > 200) throw new Error("the hand did not terminate");
      if (t.phase === "acting" && t.turn) {
        const { seat, hand } = t.turn;
        if (t.seats[seat]!.isHero) {
          const v = cloneView(this.shadow);
          v.phase = "acting";
          v.turn = { seat, hand };
          this.push({ kind: "await", dur: 0, land: 0, seat, what: "act" }, v);
          break;
        }
        const ordinal = ++this.ordinal;
        const before = this.opts.onBotDecision ? tableSnapshot(t) : null;
        const action = botAction(t, this.fidelity, this.botRng);
        if (before) this.opts.onBotDecision!({ seat, hand, action, ordinal, before, handNumber: t.handNumber });
        const d = this.durations();
        const tv = cloneView(this.shadow);
        tv.phase = "acting";
        tv.turn = { seat, hand };
        this.push({ kind: "think", dur: bjThinkTime(this.rng(), action, d.pace), land: 0, seat, hand, ordinal }, tv);
        const was = viewOf(t);
        t.act(action);
        this.ingestAction(seat, hand, action, was, ordinal);
        continue;
      }
      if (t.phase === "settled") {
        if (!this.endQueued) this.ingestDealer();
        break;
      }
      /* "dealer" never survives an `act` — the engine plays it out inside the call. */
      break;
    }
    this.startPlayback();
  }

  /**
   * After the peek (the deal, or the insurance question): a dealer natural ends the hand
   * here, and the insurance side bets are settled either way.
   */
  private afterPeek(): void {
    const t = this.t;
    const d = this.durations();
    const full = viewOf(t);
    const insured = full.seats.filter((s) => s.insurance > 0);
    if (t.phase === "settled" && t.dealerNatural) {
      /* The hole card turns: a natural. */
      let v = cloneView(this.shadow);
      this.push({ kind: "beat", dur: d.holeBeat, land: 0 }, v);
      v = cloneView(v);
      v.dealer = [...t.dealer];
      v.phase = "dealer";
      this.push({ kind: "flip", dur: d.flip, land: 0 }, v);
      this.ingestInsurance(insured.map((s) => s.index), true);
      this.ingestSettlement();
      return;
    }
    if (insured.length) this.ingestInsurance(insured.map((s) => s.index), false);
    if (t.phase === "settled" && !this.endQueued) this.ingestDealer();
  }

  private ingestInsurance(seats: number[], won: boolean): void {
    const d = this.durations();
    for (const seat of seats) {
      const s = this.t.seats[seat]!;
      const v = cloneView(this.shadow);
      v.seats[seat]!.insuranceSettled = s.insuranceSettled;
      const returned = won ? s.insurance + s.insuranceSettled : 0;
      this.push({ kind: "insurance", dur: d.potPush, land: d.potPush, seat, returned, won }, v);
    }
  }

  /** A decision's consequences, a card at a time. */
  private ingestAction(seat: number, hand: number, action: Action, was: BjView, ordinal: number): void {
    const d = this.durations();
    const now = viewOf(this.t);
    let v = cloneView(this.shadow);
    v.turn = { seat, hand };
    this.push({ kind: "act", dur: action === "stand" ? d.actionBeat : Math.round(d.actionBeat / 2), land: 0, seat, hand, action, ordinal }, v);
    const beforeHand = was.seats[seat]!.hands[hand]!;
    if (action === "hit" || action === "double") {
      if (action === "double") {
        v = cloneView(v);
        v.seats[seat]!.hands[hand]!.bet = now.seats[seat]!.hands[hand]!.bet;
        v.seats[seat]!.hands[hand]!.doubled = true;
        this.push({ kind: "chips", dur: d.betSlide, land: d.betSlide, seat, hand, amount: beforeHand.bet }, v);
      }
      const cards = now.seats[seat]!.hands[hand]!.cards;
      v = cloneView(v);
      v.seats[seat]!.hands[hand]!.cards = [...cards];
      this.push({ kind: "deal", dur: d.deal, land: d.deal, seat, hand, index: cards.length - 1, faceDown: false }, v);
    } else if (action === "split") {
      const a = now.seats[seat]!.hands[hand]!;
      const b = now.seats[seat]!.hands[hand + 1]!;
      /* The second bet goes out beside the first… */
      v = cloneView(v);
      const hs = v.seats[seat]!.hands;
      const moved = beforeHand.cards[1]!;
      hs[hand] = { ...hs[hand]!, cards: [beforeHand.cards[0]!], fromSplit: true };
      hs.splice(hand + 1, 0, { cards: [moved], bet: b.bet, doubled: false, fromSplit: true, outcome: null, settled: null });
      this.push({ kind: "split", dur: d.betSlide, land: 0, seat, hand }, v);
      v = cloneView(v);
      this.push({ kind: "chips", dur: d.betSlide, land: d.betSlide, seat, hand: hand + 1, amount: b.bet }, v);
      /* …and each half gets its second card. */
      v = cloneView(v);
      v.seats[seat]!.hands[hand]!.cards = [...a.cards];
      this.push({ kind: "deal", dur: d.dealStagger, land: d.deal, seat, hand, index: 1, faceDown: false }, v);
      v = cloneView(v);
      v.seats[seat]!.hands[hand + 1]!.cards = [...b.cards];
      this.push({ kind: "deal", dur: d.deal, land: d.deal, seat, hand: hand + 1, index: 1, faceDown: false }, v);
    }
    /* The engine moves the turn on inside `act`; the view follows once the card is down. */
    v = cloneView(this.shadow);
    v.turn = now.turn ? { ...now.turn } : null;
    this.shadow = v;
  }

  /** The dealer's hand, card by card, then the settlement. */
  private ingestDealer(): void {
    const t = this.t;
    const d = this.durations();
    let v = cloneView(this.shadow);
    v.turn = null;
    v.phase = "dealer";
    this.push({ kind: "beat", dur: d.holeBeat, land: 0 }, v);
    v = cloneView(v);
    v.dealer = [t.dealer[0]!, t.dealer[1]!];
    this.push({ kind: "flip", dur: d.flip, land: 0 }, v);
    for (let k = 2; k < t.dealer.length; k++) {
      v = cloneView(v);
      v.dealer = t.dealer.slice(0, k + 1);
      this.push({ kind: "deal", dur: d.dealerDraw, land: d.deal, seat: -1, hand: 0, index: k, faceDown: false }, v);
    }
    this.ingestSettlement();
  }

  private ingestSettlement(): void {
    this.endQueued = true;
    const t = this.t;
    const d = this.durations();
    const full = viewOf(t);
    for (const s of full.seats) {
      s.hands.forEach((h, hand) => {
        const v = cloneView(this.shadow);
        const vh = v.seats[s.index]!.hands[hand]!;
        vh.outcome = h.outcome; vh.settled = h.settled;
        vh.cards = [...h.cards]; vh.bet = h.bet; vh.doubled = h.doubled;
        const returned = h.bet + (h.settled ?? 0);
        this.push({
          kind: "settle", dur: d.settleStagger, land: returned > 0 ? d.potPush : d.potCollect,
          seat: s.index, hand, outcome: h.outcome!, returned, net: h.settled ?? 0,
        }, v);
      });
    }
    this.settle(d.potPush);
    /* The last view is the engine's own, whole: nothing presented can disagree with it. */
    this.push({ kind: "hand_over", dur: 0, land: 0 }, full);
  }

  /** The step before this one waits for its own landing (a deal's last card, the bets). */
  private settle(ms: number): void {
    const last = this.queue[this.queue.length - 1];
    if (last && last.step.dur < ms) last.step.dur = ms;
  }

  private push(step: BjStep, after: BjView): void {
    this.queue.push({ step, after });
    this.shadow = after;
  }

  /* ------------------------------------------------------------- playback --- */

  private startPlayback(): void {
    if (!this.running && !this.destroyed) this.next();
  }

  private schedule(fn: () => void, ms: number, bag: Set<unknown> = this.timers): void {
    const h = this.clock.setTimeout(() => { bag.delete(h); if (!this.destroyed) fn(); }, ms);
    bag.add(h);
  }

  private next(): void {
    if (this.destroyed) return;
    const q = this.queue.shift();
    if (!q) { this.running = false; return; }
    this.running = true;
    this.stepsSeen++;
    const { step, after } = q;
    const before = this.presented;
    this.opts.onStep?.(step, before);
    switch (step.kind) {
      case "think":
        this.presented = after;
        this.clocks = { [step.seat]: { t: 0, from: 1, ms: step.dur } };
        this.waiting = { seat: step.seat, secondsLeft: Math.ceil(step.dur / 1000) };
        this.phase = "playing";
        this.countdown(step.dur, (left) => { this.waiting = { seat: step.seat, secondsLeft: left }; this.emit(); });
        break;
      case "act":
        this.clocks = {};
        this.waiting = null;
        this.lastAction = { seat: step.seat, hand: step.hand, action: step.action };
        this.presented = after;
        break;
      case "await":
        this.presented = after;
        this.awaitingWhat = step.what;
        this.phase = "awaiting";
        this.clocks = {};
        this.waiting = null;
        this.running = false;
        this.startHumanClock();
        return;
      case "hand_over":
        this.presented = after;
        this.phase = "between";
        this.clocks = {};
        this.waiting = null;
        this.running = false;
        this.emit();
        this.opts.onHandOver?.(this.t);
        return;
      default:
        this.phase = "playing";
        if (step.land === 0) this.presented = after;
        else this.schedule(() => { this.presented = after; this.emit(); }, step.land);
    }
    this.emit();
    this.schedule(() => this.next(), step.dur);
  }

  private countdown(ms: number, each: (secondsLeft: number) => void, bag: Set<unknown> = this.timers): void {
    const whole = Math.ceil(ms / 1000);
    for (let s = 1; s < whole; s++) {
      const left = whole - s;
      this.schedule(() => each(left), ms - left * 1000, bag);
    }
  }

  /* ---------------------------------------------------------- human clock --- */

  private startHumanClock(): void {
    const hc = this.humanClock;
    if (!hc) { this.heroClock = null; this.emit(); return; }
    this.clocks = { 0: { t: 0, from: 1, ms: hc.quiet } };
    this.heroClock = { hurry: false, secondsLeft: Math.ceil((hc.quiet + hc.hurry) / 1000) };
    this.emit();
    this.countdown(hc.quiet, (left) => {
      this.heroClock = { hurry: false, secondsLeft: left + Math.ceil(hc.hurry / 1000) };
      this.emit();
    }, this.clockTimers);
    this.schedule(() => {
      this.clocks = { 0: { t: 0, from: 1, ms: hc.hurry, hurry: true } };
      const secs = Math.ceil(hc.hurry / 1000);
      this.heroClock = { hurry: true, secondsLeft: secs };
      this.opts.onTick?.(secs);
      this.emit();
      this.countdown(hc.hurry, (left) => {
        this.heroClock = { hurry: true, secondsLeft: left };
        this.opts.onTick?.(left);
        this.emit();
      }, this.clockTimers);
      this.schedule(() => this.expire(), hc.hurry, this.clockTimers);
    }, hc.quiet, this.clockTimers);
  }

  private stopHumanClock(): void {
    for (const h of this.clockTimers) this.clock.clearTimeout(h);
    this.clockTimers.clear();
  }

  /** Out of time: stand — or decline, if the question is insurance. The brief's `timeoutAction`. */
  private expire(): void {
    if (this.awaitingWhat === null) return;
    const move = bjTimeoutMove(this.awaitingWhat);
    this.opts.onExpire?.(move);
    this.act(move);
  }

  private emit(): void {
    if (this.destroyed) return;
    this.opts.onChange(this.snapshot());
  }
}

/** The move the clock makes for the hero. Exported so the room and the tests share it. */
export function bjTimeoutMove(what: "insurance" | "act"): HeroMove {
  return what === "insurance" ? "decline" : "stand";
}
